import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useI18n } from '../lib/i18n';
import { formatRSD, fmtDate } from '../lib/helpers';
import { searchKey } from '../lib/search';
import { fnError } from './SefSendDialog';
import Icon from './Icon';

// ============================================================
// ULAZNE eFAKTURE — kopija iz SEF-a (tabela purchase_invoices), osvežavanje, PDF, prihvatanje/odbijanje.
// Kupac ima 15 dana od prijema da prihvati ili odbije; posle toga se smatra prihvaćenom.
// ============================================================

const OPEN = (st) => !/^(approved|rejected|cancelled|storno|deleted)$/i.test(String(st || ''));
const STATUS = {
  new: ['Nova — čeka odgovor', 'var(--gold-600)'], seen: ['Pregledana — čeka odgovor', 'var(--gold-600)'],
  sent: ['Primljena — čeka odgovor', 'var(--gold-600)'], renotified: ['Ponovo poslata — čeka odgovor', 'var(--gold-600)'],
  deleted: ['Obrisana', 'var(--ink-500)'],
  approved: ['Prihvaćena', 'var(--forest-600)'], rejected: ['Odbijena', 'var(--red-600)'],
  cancelled: ['Otkazana', 'var(--ink-500)'], storno: ['Stornirana', 'var(--ink-500)'],
};
const statusOf = (st) => STATUS[String(st || '').toLowerCase()] || [st || '—', 'var(--ink-500)'];

// Rok za odgovor: 15 dana od dana kada je faktura poslata (primljena)
const deadline = (r) => {
  if (!r.sending_date) return null;
  const d = new Date(`${r.sending_date}T00:00:00`);
  d.setDate(d.getDate() + 15);
  return d;
};
const daysLeft = (d) => Math.ceil((d - new Date(new Date().toDateString())) / 86400000);

export default function PurchaseInvoices({ agencyId }) {
  const { tr } = useI18n();
  const year = new Date().getFullYear();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [sync, setSync] = useState({ busy: false, msg: '', err: '' });
  const [filter, setFilter] = useState('open');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [rowErr, setRowErr] = useState('');

  const load = useCallback(async () => {
    if (!agencyId) return;
    const { data } = await supabase.from('purchase_invoices').select('*').eq('agency_id', agencyId)
      .order('sending_date', { ascending: false }).limit(2000);
    setRows(data || []);
    setLoaded(true);
  }, [agencyId]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setSync({ busy: true, msg: '', err: '' });
    const { data, error } = await supabase.functions.invoke('sef', { body: { action: 'sync-purchase', agency_id: agencyId, dateFrom: `${year}-01-01` } });
    const e = await fnError(error, data);
    if (e) { setSync({ busy: false, msg: '', err: e }); return; }
    setSync({ busy: false, err: '', msg: tr(`U SEF-u: ${data.found}. Novih: ${data.created}. Osveženih: ${data.updated}.${data.failed?.length ? ` Greške: ${data.failed.length}.` : ''}`, `In SEF: ${data.found}. New: ${data.created}. Updated: ${data.updated}.`) });
    load();
  };

  const openPdf = async (r) => {
    setBusyId(r.sef_invoice_id); setRowErr('');
    const win = window.open('', '_blank');
    const { data, error } = await supabase.functions.invoke('sef', { body: { action: 'purchase-pdf', agency_id: agencyId, sef_invoice_id: r.sef_invoice_id } });
    setBusyId(null);
    const e = await fnError(error, data);
    if (e || !data?.pdf) { if (win) win.close(); setRowErr(e || tr('PDF nije dostupan.', 'PDF not available.')); return; }
    const bytes = Uint8Array.from(atob(data.pdf), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    if (win) win.location.href = url; else window.open(url, '_blank');
  };

  const respond = async (r, accepted) => {
    const who = `${r.supplier_name || ''}, ${r.invoice_number || ''}, ${formatRSD(r.total)}`;
    let comment = '';
    if (accepted) {
      if (!window.confirm(tr(`Prihvatiti fakturu u SEF-u?\n\n${who}\n\nOdgovor se šalje dobavljaču i ne može se povući.`, `Accept this invoice in SEF?\n\n${who}`))) return;
    } else {
      comment = window.prompt(tr(`Odbijanje fakture u SEF-u:\n${who}\n\nUpišite razlog (vidi ga dobavljač):`, `Reject this invoice in SEF:\n${who}\n\nReason (visible to the supplier):`), '') || '';
      if (!comment.trim()) return;
    }
    setBusyId(r.sef_invoice_id); setRowErr('');
    const { data, error } = await supabase.functions.invoke('sef', { body: { action: 'purchase-respond', agency_id: agencyId, sef_invoice_id: r.sef_invoice_id, accepted, comment } });
    setBusyId(null);
    const e = await fnError(error, data);
    if (e) { setRowErr(`${r.invoice_number}: ${e}`); return; }
    load();
  };

  const needAnswer = rows.filter((r) => OPEN(r.sef_status));
  const soon = needAnswer.filter((r) => { const d = deadline(r); return d && daysLeft(d) >= 0 && daysLeft(d) <= 5; });
  const expired = needAnswer.filter((r) => { const d = deadline(r); return d && daysLeft(d) < 0; });
  const key = searchKey(q.trim());
  const shown = rows
    .filter((r) => (filter === 'open' ? OPEN(r.sef_status) : true))
    .filter((r) => !key || searchKey(`${r.supplier_name} ${r.invoice_number} ${r.description} ${r.supplier_pib}`).includes(key));

  return (
    <div>
      <div style={s.bar}>
        <button onClick={refresh} disabled={sync.busy} style={{ ...s.primary, ...(sync.busy ? { opacity: 0.6 } : {}) }}>
          <Icon name="download" size={14} />{sync.busy ? tr('Osvežavam… (prvi put do minut)', 'Refreshing…') : tr('Osveži iz SEF-a', 'Refresh from SEF')}
        </button>
        <div style={s.seg}>
          {[['open', tr(`Čeka odgovor (${needAnswer.length})`, `Awaiting (${needAnswer.length})`)], ['all', tr(`Sve (${rows.length})`, `All (${rows.length})`)]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setFilter(v)} style={{ ...s.segBtn, ...(filter === v ? s.segOn : {}) }}>{l}</button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr('Pretraga: dobavljač, broj…', 'Search…')} style={s.search} />
      </div>
      {sync.msg && <div style={s.ok}>{sync.msg}</div>}
      {sync.err && <div style={s.err}>{sync.err}</div>}
      {rowErr && <div style={s.err}>{rowErr}</div>}
      {soon.length > 0 && (
        <div style={s.warn}>{tr(`Rok za odgovor ističe za ${soon.length} ${soon.length === 1 ? 'fakturu' : 'faktura'} (5 dana ili manje). Posle roka se faktura smatra prihvaćenom.`, `${soon.length} invoice(s) due for a response within 5 days.`)}</div>
      )}
      {expired.length > 0 && (
        <div style={s.note}>{tr(`Za ${expired.length} ${expired.length === 1 ? 'fakturu' : 'faktura'} rok od 15 dana je istekao — smatra se prihvaćenom; odgovor je i dalje moguć ako ga SEF primi.`, `${expired.length} invoice(s) past the 15-day deadline (deemed accepted).`)}</div>
      )}

      {!loaded ? null : rows.length === 0 ? (
        <p style={s.note}>{tr('Još nema preuzetih ulaznih faktura. Kliknite „Osveži iz SEF-a“.', 'No incoming invoices yet. Click “Refresh from SEF”.')}</p>
      ) : shown.length === 0 ? (
        <p style={s.note}>{tr('Nema faktura za prikaz.', 'Nothing to show.')}</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          {shown.map((r) => {
            const [label, color] = statusOf(r.sef_status);
            const d = deadline(r);
            const left = d ? daysLeft(d) : null;
            const open = OPEN(r.sef_status);
            const busy = busyId === r.sef_invoice_id;
            return (
              <div key={r.id} className="pi-row" style={s.row}>
                <div style={{ minWidth: 0 }}>
                  <div style={s.supplier}>{r.supplier_name || '—'}</div>
                  <div style={s.sub}>{r.invoice_number} · {fmtDate(r.sending_date || r.issue_date)}{r.description ? ` · ${r.description}` : ''}</div>
                </div>
                <div style={s.amount}>{formatRSD(r.total)}</div>
                <div style={{ ...s.status, color }}>
                  {label}
                  {!open && r.comment && <div style={{ fontSize: 11.5, color: 'var(--ink-500)', fontWeight: 500 }} title={r.comment}>{r.comment.length > 40 ? `${r.comment.slice(0, 40)}…` : r.comment}</div>}
                  {open && left !== null && (
                    <div style={{ fontSize: 11.5, color: left <= 5 ? 'var(--red-600)' : 'var(--ink-500)', fontWeight: left <= 5 ? 700 : 500 }}>
                      {left >= 0 ? tr(`rok: još ${left} d.`, `${left} d left`) : tr('rok istekao', 'deadline passed')}
                    </div>
                  )}
                </div>
                <div style={s.actions}>
                  <button onClick={() => openPdf(r)} disabled={busy} style={s.small} title="PDF">PDF</button>
                  {(open || /^rejected$/i.test(String(r.sef_status || ''))) && (
                    <button onClick={() => respond(r, true)} disabled={busy} style={{ ...s.small, ...s.accept }}>{tr('Prihvati', 'Accept')}</button>
                  )}
                  {open && (
                    <button onClick={() => respond(r, false)} disabled={busy} style={{ ...s.small, ...s.reject }}>{tr('Odbij', 'Reject')}</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <style>{`
        @media (max-width: 760px) {
          .pi-row { grid-template-columns: minmax(0, 1fr) auto !important; }
          .pi-row > div:nth-child(3) { grid-column: 1; }
          .pi-row > div:nth-child(4) { grid-column: 2; }
        }
      `}</style>
    </div>
  );
}

const s = {
  bar: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 },
  primary: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 34, border: 'none', background: 'var(--teal-800)', color: '#fff', borderRadius: 8, padding: '0 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  seg: { display: 'flex', background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 8, padding: 2 },
  segBtn: { border: 'none', background: 'transparent', borderRadius: 6, padding: '6px 10px', fontSize: 12.5, fontWeight: 600, color: 'var(--ink-700)', cursor: 'pointer' },
  segOn: { background: 'var(--surface)', boxShadow: '0 0 0 1px var(--line)' },
  search: { flex: '1 1 180px', minWidth: 0, height: 34, border: '1px solid var(--line)', borderRadius: 8, padding: '0 10px', fontSize: 13 },
  ok: { fontSize: 13, background: 'var(--lavender-100)', color: 'var(--ink-900)', borderRadius: 8, padding: '8px 10px', marginBottom: 8 },
  err: { fontSize: 12.5, background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 8, padding: '8px 10px', marginBottom: 8, wordBreak: 'break-word' },
  warn: { fontSize: 12.5, background: 'var(--gold-100)', color: 'var(--ink-900)', borderRadius: 8, padding: '8px 10px', marginBottom: 8 },
  note: { fontSize: 13, color: 'var(--ink-500)' },
  row: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 130px 170px 190px', gap: 12, alignItems: 'center', padding: '10px 4px', borderBottom: '1px solid var(--line)' },
  supplier: { fontSize: 13.5, fontWeight: 700, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  sub: { fontSize: 12, color: 'var(--ink-500)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  amount: { fontSize: 13.5, fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  status: { fontSize: 12.5, fontWeight: 700 },
  actions: { display: 'flex', gap: 6, justifyContent: 'flex-end' },
  small: { border: '1px solid var(--line)', background: 'var(--surface)', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: 'var(--ink-700)' },
  accept: { borderColor: 'var(--forest-600)', color: 'var(--forest-600)' },
  reject: { borderColor: 'var(--red-600)', color: 'var(--red-600)' },
};
